import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { marketplaceOptionsList } from "./marketplaceData";
import Flag from "./components/Flag";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./components/ui";
import { Loader2, PlusCircle, RefreshCw, Trash2, Wifi, WifiOff } from "lucide-react";
import { cn } from "./lib/utils";

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, "");
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://127.0.0.1:8000";
  }
  return "/api";
};
const API_URL = resolveApiBase();

const defaultMarketplace = marketplaceOptionsList[0];

const Marketplaces = ({ token, onMarketplaceAdd }) => {
    const [marketplaces, setMarketplaces] = useState([]);
    const [newMarketplace, setNewMarketplace] = useState({
    name: defaultMarketplace.name,
    country_code: defaultMarketplace.code,
    sp_api_refresh_token: "",
    sp_api_lwa_app_id: "",
    sp_api_lwa_client_secret: "",
    sp_api_aws_access_key: "",
    sp_api_aws_secret_key: ""
    });
  const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
  const [selectedMarketplaceCode, setSelectedMarketplaceCode] = useState(defaultMarketplace.code);
    const [testingMarketplace, setTestingMarketplace] = useState(null);

  const fetchMarketplaces = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/marketplaces/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
      setMarketplaces(response.data || []);
        } catch (err) {
            console.error("Failed to fetch marketplaces", err);
      setError("Unable to load marketplaces. Please try again later.");
        }
  }, [token]);

    const handleTestConnection = async (marketplace) => {
        setTestingMarketplace(marketplace.id);
        try {
      const response = await axios.get(`${API_URL}/sp-api/${marketplace.id}/test-connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
            await fetchMarketplaces();
            
      if (response.data.status === "success") {
        setError("");
            } else {
        setError(`Connection test failed: ${response.data.message}\n${response.data.error || ""}`);
            }
        } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || "Failed to test connection";
            setError(`Connection test failed: ${errorMessage}`);
        } finally {
            setTestingMarketplace(null);
        }
    };

    useEffect(() => {
        fetchMarketplaces();
  }, [fetchMarketplaces]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewMarketplace((prev) => ({ ...prev, [name]: value }));
    };

  const handleMarketplaceSelect = (value) => {
    const option = marketplaceOptionsList.find((m) => m.code === value) || defaultMarketplace;
    setSelectedMarketplaceCode(option.code);
    setNewMarketplace((prev) => ({ ...prev, name: option.name, country_code: option.code }));
    };

    const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this marketplace?")) {
            return;
        }
        
        try {
            await axios.delete(`${API_URL}/marketplaces/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
      setMarketplaces((prev) => prev.filter((m) => m.id !== id));
      onMarketplaceAdd?.();
        } catch (err) {
      setError("Failed to remove marketplace.");
        }
    };

  const handleSubmit = async (event) => {
    event.preventDefault();
        setLoading(true);
    setError("");
        try {
            const response = await axios.post(`${API_URL}/marketplaces/`, newMarketplace, {
                headers: { Authorization: `Bearer ${token}` }
            });
      setMarketplaces((prev) => [...prev, response.data]);
      onMarketplaceAdd?.();
            setNewMarketplace({
        name: defaultMarketplace.name,
        country_code: defaultMarketplace.code,
        sp_api_refresh_token: "",
        sp_api_lwa_app_id: "",
        sp_api_lwa_client_secret: "",
        sp_api_aws_access_key: "",
        sp_api_aws_secret_key: ""
            });
      setSelectedMarketplaceCode(defaultMarketplace.code);
        } catch (err) {
      setError(err.response?.data?.detail || "Failed to add marketplace.");
        } finally {
            setLoading(false);
        }
    };

    return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">Marketplace Connections</h1>
        <p className="text-sm text-muted-foreground">
          Link your Amazon marketplaces to enable order and invoice automation.
        </p>
            </div>

            {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
            )}

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card className="border-border/60 bg-card/70">
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Connected marketplaces</CardTitle>
              <CardDescription>Manage existing connections and verify API credentials.</CardDescription>
                        </div>
            <Button variant="ghost" size="sm" onClick={fetchMarketplaces} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh list
            </Button>
          </CardHeader>
          <CardContent>
                            {marketplaces.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border/60 bg-background/60 p-10 text-center text-sm text-muted-foreground">
                <div className="text-lg font-semibold text-foreground">No marketplaces connected</div>
                <p className="max-w-sm">
                  Add your first marketplace to start syncing orders and generating invoices.
                </p>
                                </div>
                            ) : (
              <ScrollArea className="max-h-[520px] pr-3">
                <div className="space-y-4">
                  {marketplaces.map((marketplace) => {
                    const optionMeta = marketplaceOptionsList.find((opt) => opt.code === marketplace.country_code);
                    const isConnected = marketplace.is_connected;
                    const lastUpdated = marketplace.updated_at
                      ? new Date(marketplace.updated_at).toLocaleString()
                      : "—";
                    const statusLabel = isConnected ? "Active" : "Disconnected";
                    const statusHelper = isConnected ? "Credentials verified" : "Check SP-API settings";
                    return (
                      <div
                        key={marketplace.id}
                        className="rounded-xl border border-border/60 bg-background/70 p-5 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex items-start gap-4">
                            <div className="hidden sm:block">
                                                <Flag code={marketplace.country_code} />
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-base font-semibold text-foreground">{marketplace.name}</span>
                                <Badge
                                  variant={isConnected ? "secondary" : "outline"}
                                  className={cn(
                                    "flex items-center gap-1",
                                    isConnected ? "text-emerald-700" : "text-rose-600"
                                  )}
                                >
                                  {isConnected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                                  {isConnected ? "Connected" : "Needs setup"}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{marketplace.country_code}</p>
                              <p className="text-xs text-muted-foreground/80">
                                Marketplace ID: {optionMeta?.id || "N/A"}
                                                    </p>
                                                </div>
                                            </div>
                          <div className="flex flex-col items-start gap-2 text-sm text-muted-foreground">
                            <div>
                              <span className="font-medium text-foreground">Status:</span> {statusLabel}
                                        </div>
                            <div>
                              <span className="font-medium text-foreground">Last updated:</span> {lastUpdated}
                                            </div>
                            <div>
                              <span className="font-medium text-foreground">Notes:</span> {statusHelper}
                                            </div>
                                        </div>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                              variant="outline"
                              size="sm"
                                                onClick={() => handleTestConnection(marketplace)}
                                                disabled={testingMarketplace === marketplace.id}
                              className="gap-2"
                            >
                              {testingMarketplace === marketplace.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Wifi className="h-4 w-4" />
                              )}
                              {testingMarketplace === marketplace.id ? "Testing..." : "Test connection"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                                                onClick={() => handleDelete(marketplace.id)}
                              className="gap-2 text-rose-600 hover:text-rose-500"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </Button>
                                    </div>
                        </div>
                    </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/70">
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Add New Marketplace</CardTitle>
              <CardDescription>
                Add a new marketplace to your account to start syncing orders and generating invoices.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchMarketplaces} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh list
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    Marketplace Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={newMarketplace.name}
                    onChange={handleInputChange}
                    className="col-span-3"
                  />
                        </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="country_code" className="text-right">
                    Country Code
                  </Label>
                  <Select onValueChange={handleMarketplaceSelect} defaultValue={selectedMarketplaceCode}>
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                      {marketplaceOptionsList.map((option) => (
                        <SelectItem key={option.code} value={option.code}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="sp_api_refresh_token" className="text-right">
                    SP-API Refresh Token
                  </Label>
                  <Input
                    id="sp_api_refresh_token"
                    name="sp_api_refresh_token"
                    type="password"
                    value={newMarketplace.sp_api_refresh_token}
                    onChange={handleInputChange}
                    className="col-span-3"
                  />
                            </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="sp_api_lwa_app_id" className="text-right">
                    SP-API LWA App ID
                  </Label>
                  <Input
                                    id="sp_api_lwa_app_id" 
                                    name="sp_api_lwa_app_id" 
                                    value={newMarketplace.sp_api_lwa_app_id} 
                                    onChange={handleInputChange} 
                    className="col-span-3"
                                />
                            </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="sp_api_lwa_client_secret" className="text-right">
                    SP-API LWA Client Secret
                  </Label>
                  <Input
                                    id="sp_api_lwa_client_secret" 
                                    name="sp_api_lwa_client_secret" 
                                    type="password" 
                                    value={newMarketplace.sp_api_lwa_client_secret} 
                                    onChange={handleInputChange} 
                    className="col-span-3"
                                />
                            </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="sp_api_aws_access_key" className="text-right">
                    SP-API AWS Access Key
                  </Label>
                  <Input
                                    id="sp_api_aws_access_key" 
                                    name="sp_api_aws_access_key" 
                                    value={newMarketplace.sp_api_aws_access_key} 
                                    onChange={handleInputChange} 
                    className="col-span-3"
                                />
                            </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="sp_api_aws_secret_key" className="text-right">
                    SP-API AWS Secret Key
                  </Label>
                  <Input
                                    id="sp_api_aws_secret_key" 
                                    name="sp_api_aws_secret_key" 
                                    type="password" 
                                    value={newMarketplace.sp_api_aws_secret_key} 
                                    onChange={handleInputChange} 
                    className="col-span-3"
                                />
                </div>
              </div>
              <Button type="submit" className="w-full">
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlusCircle className="mr-2 h-4 w-4" />
                )}
                {loading ? "Adding..." : "Add Marketplace"}
              </Button>
            </form>
          </CardContent>
        </Card>
            </div>
        </div>
    );
};

export default Marketplaces;